import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

const API = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading/checking, false = unauthenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  // Auto-refresh the access token on 401 and retry the original request once.
  useEffect(() => {
    let isRefreshing = false;
    let queue = [];

    const processQueue = (error) => {
      queue.forEach((p) => (error ? p.reject(error) : p.resolve()));
      queue = [];
    };

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const url = originalRequest?.url || "";

        // Do not try to refresh for the auth endpoints themselves
        const isAuthEndpoint =
          url.includes("/api/auth/login") ||
          url.includes("/api/auth/refresh") ||
          url.includes("/api/auth/me");

        if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          if (isRefreshing) {
            // Wait for the ongoing refresh to finish, then retry
            return new Promise((resolve, reject) => {
              queue.push({ resolve, reject });
            }).then(() => axios(originalRequest));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
            processQueue(null);
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError);
            setUser(false);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkUser = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
        { withCredentials: true }
      );
      setUser(response.data);
    } catch (e) {
      // Access token may have expired - try one refresh before giving up
      try {
        await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        const retry = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        setUser(retry.data);
      } catch (e2) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (e) {
      console.error("Error during server logout", e);
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
